import { Text } from '@scality/core-ui';
import type { AIToolComponent } from '../../chat-messages/GuardianMarkdown';
import { InstanceValue } from '../../InstanceValue';
import { type ExportTable, WidgetActions, WidgetExportMenu } from '../../widget-actions';
import { NoData } from '../NoData';
import { MetricCard, type MetricCardStatus } from '../shared/MetricCard';
import { MetricCardGrid } from '../shared/MetricCardGrid';

const STATUSES: MetricCardStatus[] = ['headline', 'neutral', 'healthy', 'warning', 'critical'];

type MetricCardItem = {
  label: string;
  value: number;
  /** Rendered small under the value, so a long unit cannot overflow the 4em number. */
  unit?: string;
  status?: MetricCardStatus;
};

type MetricCardsProps = {
  title?: string;
  cards: MetricCardItem[];
};

/**
 * Compact notation, because the card is the constraint: at 4em, `1,240,000` overflows a card in a
 * side panel where `1.2M` fits. Precision belongs in the chart the reader opens next.
 *
 * Neutral `'en'`, not `'en-GB'`: CLDR's British compact suffixes are lower case — `1.2m`, `1.2bn` —
 * which reads as a unit rather than a magnitude on a card whose unit sits right underneath.
 */
const VALUE_FORMAT = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });

const DEFAULT_TITLE = 'Metrics';

/** The value as it arrived: the compact notation above fits a card, it does not compute. */
const buildMetricsTable = ({ title, cards }: MetricCardsProps): ExportTable => {
  // Both are optional per card, and a column of nothing but blanks is worse than no column at all.
  const hasUnits = cards.some((card) => card.unit);
  const hasStatuses = cards.some((card) => card.status);

  return {
    name: title ?? DEFAULT_TITLE,
    columns: ['metric', 'value', ...(hasUnits ? ['unit'] : []), ...(hasStatuses ? ['status'] : [])],
    rows: cards.map((card) => [
      card.label,
      card.value,
      ...(hasUnits ? [card.unit ?? null] : []),
      ...(hasStatuses ? [card.status ?? null] : []),
    ]),
  };
};

/**
 * A row of headline numbers, for any values at all.
 *
 * This is the vitals dashboard's card layout without the vitals: the payload states each value and
 * how it reads (`status`), so a capacity in TB, an alert count and a score out of 100 all render
 * here. `VitalsDashboard` stays the vitals-specific widget — it derives its status from the vitals
 * threshold and writes a headline about scores, neither of which generalises.
 */
export const MetricCards: AIToolComponent<MetricCardsProps> = ({ params }) => {
  if (params.cards.length === 0) {
    return <NoData title={params.title ?? DEFAULT_TITLE} />;
  }

  return (
    <MetricCardGrid
      header={params.title ? <span>{params.title}</span> : undefined}
      actions={
        <WidgetActions>
          <WidgetExportMenu
            label={`Export ${params.title ?? DEFAULT_TITLE}`}
            buildTable={() => buildMetricsTable(params)}
          />
        </WidgetActions>
      }
    >
      {params.cards.map((card) => (
        <MetricCard
          key={card.label}
          title={<InstanceValue value={card.label} />}
          value={VALUE_FORMAT.format(card.value)}
          status={card.status ?? 'neutral'}
          caption={
            card.unit ? (
              <Text variant="Smaller" color="textSecondary">
                {card.unit}
              </Text>
            ) : undefined
          }
        />
      ))}
    </MetricCardGrid>
  );
};

MetricCards.description =
  'Displays a row of headline numbers, one card per value. Use it for current values that need no ' +
  'trend — a capacity, a count, a score. Set `status` to colour a value that needs a comment ' +
  '(critical, warning, healthy), or `headline` for the brand gradient; leave it out for a plain reading.';

MetricCards.schema = {
  type: 'object',
  properties: {
    title: { type: 'string', description: 'Optional headline above the cards' },
    cards: {
      type: 'array',
      minItems: 1,
      maxItems: 9,
      items: {
        type: 'object',
        properties: {
          label: { type: 'string', description: 'What the number measures' },
          value: { type: 'number' },
          unit: { type: 'string', description: "Unit shown under the value, e.g. '%', 'TB', 'alerts'" },
          status: {
            type: 'string',
            enum: STATUSES,
            description: 'How the value reads. Omit for a value that needs no comment.',
          },
        },
        required: ['label', 'value'],
      },
    },
  },
  required: ['cards'],
};

MetricCards.displayInPanel = true;
